import { Link } from "@reach/router";
import useAuth from "hooks/useAuth";
import React from "react";
import { ArrowRight, Check, Globe, Layout, Package, Plus } from "react-feather";
import logo from "assets/supplypro/logo.png";
import storefront from "assets/supplypro/storefront.png";
import styles from "./home.module.css";

export default function Home(props: ReachPathProps) {
  const { user } = useAuth();
  const registrationUrl = `${process.env.REACT_APP_HOME_URL || "https://materialspro.ng"}/signup.html?orgType=supplier`;
  const actionUrl = user ? "/suppliers/dashboard" : registrationUrl;
  const actionLabel = user ? "Go to your dashboard" : "Create your free store";

  return (
    <div className={styles.page}>
      <a className={styles.skipLink} href="#main">Skip to content</a>
      <header className={styles.header}>
        <div className={styles.container + " " + styles.navigation}>
          <Link to="/" className={styles.brand} aria-label="SupplyPro home">
            <img src={logo} alt="" width="42" height="42" />
            <span>supplypro<span className={styles.brandDot}>.</span></span>
          </Link>
          <nav className={styles.navLinks} aria-label="Main navigation">
            <a href="#how-it-works">How it works</a>
            <a href="#your-store">Your storefront</a>
            <a href="#questions">FAQs</a>
          </nav>
          <div className={styles.navActions}>
            <Link to="/login" className={styles.login}>Log in</Link>
            <a href={actionUrl} className={styles.button}>
              {user ? "Dashboard" : "Get started"} <ArrowRight size={16} aria-hidden="true" />
            </a>
          </div>
        </div>
      </header>

      <main id="main">
        <section className={styles.hero}>
          <div className={styles.container + " " + styles.heroGrid}>
            <div className={styles.heroCopy}>
              <p className={styles.eyebrow}><span className={styles.statusDot} /> BUILT FOR THE PEOPLE WHO SUPPLY THE BUILD</p>
              <h1>Your materials.<br />Your business.<br /><span>Your online store.</span></h1>
              <p className={styles.intro}>
                From your yard to their next project. Give your customers a place
                to browse and shop your building materials, with a free website
                that’s yours.
              </p>
              <div className={styles.heroActions}>
                <a href={actionUrl} className={styles.button}>
                  {actionLabel} <ArrowRight size={18} aria-hidden="true" />
                </a>
                <a href="#your-store" className={styles.textLink}>Take a look around <span aria-hidden="true">↗</span></a>
              </div>
              <p className={styles.reassurance}><Check size={15} aria-hidden="true" /> Free storefront. Your own web address. No coding.</p>
            </div>

            <div className={styles.heroVisual}>
              <div className={styles.visualLabel}><span /> YOUR BUSINESS, OPEN ONLINE</div>
              <div className={styles.browser}>
                <div className={styles.browserBar}>
                  <span className={styles.browserDots} aria-hidden="true"><i /><i /><i /></span>
                  <span><Globe size={12} aria-hidden="true" /> yourbusiness.supplypro.ng</span>
                  <span aria-hidden="true">↗</span>
                </div>
                <div className={styles.shopIntro}>
                  <span className={styles.shopEyebrow}>QUALITY MATERIALS. ONE PLACE.</span>
                  <h2>Good projects start<br />with the right supplier.</h2>
                  <p>Browse our building materials</p>
                </div>
                <img className={styles.storefrontPreview} src={storefront}
                  alt="Example storefront showing granite and sand products with prices and buy buttons"
                  width="1330" height="864" />
                <div className={styles.shopFooter}><Check size={13} aria-hidden="true" /> Powered by SupplyPro <span>Built around your business.</span></div>
              </div>
              <div className={styles.freeNote}><span>₦0</span><div>for your storefront.<br /><strong>Yes, really.</strong></div></div>
              <p className={styles.visualCaption}>A little preview of what’s possible. A whole new way to be found.</p>
            </div>
          </div>
        </section>

        <section className={styles.materialStrip} aria-label="Who SupplyPro is for">
          <div className={styles.container + " " + styles.materialRow}>
            <span>WHATEVER YOU SUPPLY</span>
            <p>Cement <i /> Sand &amp; granite <i /> Steel <i /> Roofing <i /> Tiles <i /> And more</p>
          </div>
        </section>

        <section id="how-it-works" className={styles.section}>
          <div className={styles.container}>
            <div className={styles.sectionHeading}>
              <div><p className={styles.eyebrow}>LESS SETUP. MORE SELLING.</p><h2>From supplier to<br />online store owner.</h2></div>
              <p>You know your materials. We take care of the website.<br />Here’s how you bring your business online.</p>
            </div>
            <div className={styles.steps}>
              {[
                { number: "01", icon: <Globe />, title: "Make it yours", text: "Set up your supplier profile, add your business name and logo, and get your own SupplyPro web address." },
                { number: "02", icon: <Package />, title: "Stock your storefront", text: "Add your materials, photos and prices. Give customers a clear view of what you sell, all in one place." },
                { number: "03", icon: <ArrowRight />, title: "Share it. Start selling.", text: "Put your store link on WhatsApp, social media or your business card. Your customers can browse and shop directly." },
              ].map((step) => (
                <article className={styles.step} key={step.number}>
                  <div className={styles.stepTop}><span>{step.number}</span><span aria-hidden="true">{step.icon}</span></div>
                  <h3>{step.title}</h3><p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="your-store" className={styles.storeSection}>
          <div className={styles.container + " " + styles.storeGrid}>
            <div className={styles.styleBoard} aria-label="Storefront style illustration">
              <div className={styles.styleBoardTop}><Layout size={18} aria-hidden="true" /><span>YOUR STORE, YOUR WAY</span></div>
              <div className={styles.styleOptions}>
                <div className={styles.styleOption}><div className={styles.miniHeader} /><div className={styles.miniHero}>Built on<br />solid ground.</div><div className={styles.miniProducts}><i /><i /><i /></div><span>Clean &amp; simple</span></div>
                <div className={styles.styleOption + " " + styles.darkOption}><div className={styles.miniHeader} /><div className={styles.miniHero}>Materials for<br />what’s next.</div><div className={styles.miniProducts}><i /><i /><i /></div><span>Bold &amp; distinctive</span></div>
              </div>
              <p className={styles.boardCaption}>Different looks. The same business: yours.</p>
              <span className={styles.illustrationNote}>Illustrative layouts</span>
            </div>
            <div className={styles.storeCopy}>
              <p className={styles.eyebrow}>NOT JUST ANOTHER LISTING</p>
              <h2>A proper website.<br />With your name on it.</h2>
              <p>Your business has its own identity. Your online store should too.
                Choose a storefront style that feels right for your brand, not a one-size-fits-all page.</p>
              <ul className={styles.featureList}>
                <li><Check size={18} aria-hidden="true" /> Choose from different storefront designs</li>
                <li><Check size={18} aria-hidden="true" /> Show off your logo, products and prices</li>
                <li><Check size={18} aria-hidden="true" /> Give customers one link to browse and shop</li>
              </ul>
              <a href={actionUrl} className={styles.textLink}>Build your storefront <ArrowRight size={17} aria-hidden="true" /></a>
            </div>
          </div>
          <div className={styles.container}>
            <div className={styles.domainPanel}>
              <div className={styles.domainIcon}><Globe size={25} aria-hidden="true" /></div>
              <div><h3>A bigger vision for your brand?</h3><p>Commission a custom design, bring a domain you already own, or buy a new one. Start free and make it even more yours when you’re ready.</p></div>
              <span className={styles.optionalTag}>OPTIONAL EXTRAS</span>
            </div>
          </div>
        </section>

        <section id="questions" className={styles.section}>
          <div className={styles.container + " " + styles.faqGrid}>
            <div><p className={styles.eyebrow}>A FEW THINGS TO KNOW</p><h2>Good questions.<br />Straight answers.</h2></div>
            <div className={styles.faqList}>
              {[
                ["Is my storefront really free?", "Yes. You can set up a free supplier storefront, add your products and share your own SupplyPro web address. A commissioned custom design or a custom domain is optional and costs extra."],
                ["Do I need to know how to build a website?", "No coding or web design experience needed. Set up your business profile, choose a storefront style and add your products."],
                ["How do customers find my store?", "Your store has its own address, like yourbusiness.supplypro.ng. Share the link with customers on WhatsApp, social media, business cards or wherever you promote your business."],
                ["Can I use my own domain?", "Yes. You can arrange to transfer a domain you already own or buy a new one for your storefront. You can also commission a custom customer experience for your brand."],
              ].map(([question, answer]) => (
                <details key={question}><summary>{question}<Plus size={18} aria-hidden="true" /></summary><p>{answer}</p></details>
              ))}
            </div>
          </div>
        </section>

        <section className={styles.closing}>
          <div className={styles.container + " " + styles.closingInner}>
            <div><p className={styles.eyebrow}>YOUR NEXT CHAPTER STARTS HERE</p><h2>You supply the materials.<br />We’ll supply the storefront.</h2><p>Take your business online. Keep building what’s yours.</p></div>
            <div><a href={actionUrl} className={styles.button + " " + styles.lightButton}>{actionLabel}<ArrowRight size={18} aria-hidden="true" /></a><span className={styles.closingNote}>Made for suppliers. Free to get started.</span></div>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={styles.container + " " + styles.footerInner}>
          <Link to="/" className={styles.brand}><img src={logo} alt="" width="34" height="34" /><span>supplypro<span className={styles.brandDot}>.</span></span></Link>
          <p>Helping suppliers build their business online.</p>
          <span>© {new Date().getFullYear()} SupplyPro</span>
        </div>
      </footer>
    </div>
  );
}
